import { useState } from "react";
import { authAPI } from "../lib/api";
import { useLang } from "../lib/i18n";
import LangToggle from "../components/LangToggle";
import InstallButton from "../components/InstallPrompt";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Eye, EyeOff, UserPlus, LogIn, AlertTriangle, ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage({ onLogin, onPrivacy, onTerms, onFaq }) {
  var { t } = useLang();
  var [view, setView] = useState("login");
  var [email, setEmail] = useState("");
  var [password, setPassword] = useState("");
  var [name, setName] = useState("");
  var [confirmPassword, setConfirmPassword] = useState("");
  var [showPwd, setShowPwd] = useState(false);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState("");

  function resetForm() { setEmail(""); setPassword(""); setName(""); setConfirmPassword(""); setShowPwd(false); setError(""); }
  function switchView(v) { resetForm(); setView(v); }

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    if (!email.trim()) return setError(t.enterEmail);
    if (!password.trim()) return setError(t.enterPassword);
    setLoading(true);
    try {
      var isReg = view === "register";
      var res;
      if (isReg) {
        if (password.length < 6) { setLoading(false); return setError(t.minPassword); }
        res = await authAPI.register({ email, password, name });
        toast.success(t.registerSuccess);
      } else {
        res = await authAPI.login({ email, password });
        toast.success(t.loginSuccess);
      }
      localStorage.setItem("pm_token", res.data.token);
      localStorage.setItem("pm_user", JSON.stringify(res.data.user));
      onLogin(res.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || t.connectionError);
    } finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault();
    setError("");
    if (!email.trim()) return setError(t.enterEmail);
    setLoading(true);
    try {
      await authAPI.resetPassword({ email });
      toast.success(t.resetSent || "Se l'email è registrata, riceverai istruzioni per il recupero.");
      switchView("login");
    } catch (err) {
      toast.success(t.resetSent || "Se l'email è registrata, riceverai istruzioni per il recupero.");
      switchView("login");
    } finally { setLoading(false); }
  }

  if (view === "reset") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]" data-testid="reset-page">
        <div className="absolute top-4 right-4"><LangToggle /></div>
        <div className="w-full max-w-[420px] bg-white border border-slate-200 rounded-3xl p-12 shadow-lg">
          <div className="flex justify-center mb-4">
            <img src={process.env.PUBLIC_URL + "/padlock-icon.png"} alt="Logo" className="w-20 h-20 drop-shadow-md" />
          </div>
          <h1 className="text-2xl font-bold text-center text-slate-800 mb-2">{t.resetTitle}</h1>
          <p className="text-sm text-slate-500 text-center mb-6">{t.resetDescription || "Inserisci la tua email per ricevere le istruzioni di recupero."}</p>
          <form onSubmit={handleReset} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
              <Input data-testid="reset-email-input" type="email" value={email} onChange={function(e){setEmail(e.target.value)}} placeholder="tua@email.com" className="bg-slate-50 border-slate-200 text-slate-800 h-12" />
            </div>
            {error && <p className="text-red-500 text-sm text-center" data-testid="reset-error">{error}</p>}
            <Button data-testid="reset-submit-button" type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-green-700 to-green-800 text-white font-semibold">
              {loading ? t.loading : (t.resetButton || "Invia richiesta")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button data-testid="back-to-login-button" onClick={function(){switchView("login")}} className="text-green-700 hover:text-green-800 text-sm font-semibold inline-flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> {t.backToLogin}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F8FAFC]" data-testid="login-page">
      <div className="absolute top-4 right-4"><LangToggle /></div>
      <div className="w-full max-w-[420px] bg-white border border-slate-200 rounded-3xl p-12 shadow-lg">
        <div className="flex flex-col items-center mb-6">
          <img src={process.env.PUBLIC_URL + "/padlock-icon.png"} alt="Gestione Password" className="w-28 h-28 sm:w-36 sm:h-36 object-contain drop-shadow-md" />
          <h1 className="text-2xl font-bold text-slate-800 mt-3">{t.gestione}</h1>
          <p className="text-xs text-slate-400 mt-1">{t.byAcaridi}</p>
          <img src={process.env.PUBLIC_URL + "/logo_acaridi_digital_app.png"} alt="AC Digital App" className="w-8 h-8 rounded-full object-contain mt-2 opacity-70" />
        </div>

        <h2 className="text-lg font-bold text-center text-slate-800 mb-1" data-testid="auth-title">
          {view === "register" ? t.register : t.login}
        </h2>
        <p className="text-sm text-slate-500 text-center mb-6">
          {view === "register" ? t.preloaded : t.unlock}
        </p>

        <form onSubmit={handleLogin} className="space-y-4">
          {view === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.name}</Label>
              <Input data-testid="register-name-input" value={name} onChange={function(e){setName(e.target.value)}} placeholder={t.name} className="bg-slate-50 border-slate-200 text-slate-800 h-12" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.email}</Label>
            <Input data-testid="email-input" type="email" value={email} onChange={function(e){setEmail(e.target.value)}} placeholder="tua@email.com" className="bg-slate-50 border-slate-200 text-slate-800 h-12" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.password}</Label>
            <div className="relative">
              <Input data-testid="password-input" type={showPwd?"text":"password"} value={password} onChange={function(e){setPassword(e.target.value)}} className="bg-slate-50 border-slate-200 text-slate-800 h-12 pr-12" />
              <button type="button" data-testid="toggle-password-visibility" onClick={function(){setShowPwd(!showPwd)}} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          {error && <p className="text-red-500 text-sm text-center" data-testid="auth-error">{error}</p>}
          <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-green-700 to-green-800 hover:from-green-800 hover:to-green-900 text-white font-semibold text-base shadow-md">
            {loading ? t.loading : view === "register" ? <><UserPlus className="w-5 h-5 mr-2" /> {t.register}</> : <><LogIn className="w-5 h-5 mr-2" /> {t.login}</>}
          </Button>
        </form>

        {view === "login" && (
          <div className="mt-4 text-center">
            <button data-testid="forgot-password-button" onClick={function(){switchView("reset")}} className="text-slate-400 hover:text-slate-600 text-sm">{t.forgotPassword}</button>
          </div>
        )}
        <div className="mt-4 text-center">
          <button data-testid="toggle-auth-mode" onClick={function(){switchView(view==="register"?"login":"register")}} className="text-green-700 hover:text-green-800 text-sm font-semibold">
            {view === "register" ? t.hasAccount : t.firstTime}
          </button>
        </div>
        <div className="mt-4 flex justify-center"><InstallButton /></div>
        <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-slate-400">
          <a href="/faq" data-testid="faq-link" onClick={function(event){ event.preventDefault(); onFaq(); }} className="hover:text-slate-600">FAQ</a>
          <span>·</span>
          <a href="/privacy" data-testid="privacy-link" onClick={function(event){ event.preventDefault(); onPrivacy(); }} className="hover:text-slate-600">Privacy Policy</a>
          <span>·</span>
          <a href="/terms" data-testid="terms-link" onClick={function(event){ event.preventDefault(); onTerms(); }} className="hover:text-slate-600">Termini di Servizio</a>
        </div>
      </div>
    </div>
  );
}
